import {
  PERMISSIONS_ENUM,
  ROUTE_COTTON_ANALYTICAL,
  ROUTE_DOCS_LANDS,
  ROUTE_DOCS_MONITORING,
  ROUTE_DOCS_PROJECTS,
  ROUTE_DOCS_STEWARDSHIP,
  ROUTE_TEACHER_STUDENT,
  ROUTE_TEACHER_STUDENT_STAT,
} from '@/common/constants';
import { LandActiveIcon } from '@/common/icons/LandActiveIcon.tsx';
import { LandIcon } from '@/common/icons/LandIcon.tsx';
import { LogoShortIcon } from '@/common/icons/LogoShortIcon.tsx';
import MonitoringIcon from '@/common/icons/Monitoring.tsx';
import MonitoringIconActive from '@/common/icons/MonitoringActive.tsx';
import { ProjectActiveIcon } from '@/common/icons/ProjectActiveIcon.tsx';
import { ProjectIcon } from '@/common/icons/ProjectIcon.tsx';
import SidebarArrowBgIcon from '@/common/icons/SidebarArrowBgIcon.tsx';
import TagUserIcon from '@/common/icons/TagUser.tsx';
import TagUserActiveIcon from '@/common/icons/TagUserActiveIcon.tsx';
import { Button } from '@/components/ui/button.tsx';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar.tsx';
import useStore from '@/lib/store.ts';
import { cn } from '@/lib/utils.ts';
import {
  ChevronDown,
  ChevronRight,
  GraduationCap,
  LayoutList,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation, useParams } from 'react-router-dom';
import { Language } from '../language/Language.tsx';
import { ReportsDialog } from '../report-dialog/ReportDialog.tsx';
import { LayoutSidebarUser } from './LayoutSidebarUser.tsx';
import './sidebar.css';

interface ISidebarItem {
  title: string;
  path: string;
  badge?: number;
  icon?: any;
  active_icon?: any;
  sub_items?: ISidebarItem[];
  permissions?: string[];
}

const navigationItems: ISidebarItem[] = [
  {
    title: 'land.plots',
    path: ROUTE_DOCS_LANDS,
    icon: <LandIcon />,
    active_icon: <LandActiveIcon />,
    permissions: [PERMISSIONS_ENUM.VIEW_LANDS],
  },
  {
    title: 'lands.depending.project',
    path: ROUTE_DOCS_PROJECTS,
    icon: <ProjectIcon />,
    active_icon: <ProjectActiveIcon />,
    permissions: [PERMISSIONS_ENUM.VIEW_PROJECTS],
  },
  {
    title: 'monitoring',
    path: ROUTE_DOCS_MONITORING,
    icon: <MonitoringIcon />,
    active_icon: <MonitoringIconActive />,
    permissions: [PERMISSIONS_ENUM.VIEW_STATISTICS],
  },
  {
    title: 'responsible.personnel',
    path: ROUTE_DOCS_STEWARDSHIP,
    icon: <TagUserIcon />,
    active_icon: <TagUserActiveIcon />,
    permissions: [PERMISSIONS_ENUM.VIEW_STATISTICS],
  },
  {
    title: 'teacher.student',
    path: ROUTE_TEACHER_STUDENT,
    icon: <GraduationCap color="white" />,
    active_icon: <GraduationCap color="#2ED0FF" />,
    permissions: [PERMISSIONS_ENUM.VIEW_STATISTICS],
    sub_items: [
      {
        title: 'teacher.student',
        path: ROUTE_TEACHER_STUDENT,
        permissions: [PERMISSIONS_ENUM.VIEW_STATISTICS],
      },
      {
        title: 'rated.contours',
        path: ROUTE_TEACHER_STUDENT_STAT,
        permissions: [PERMISSIONS_ENUM.VIEW_STATISTICS],
      },
    ],
  },
  {
    title: 'cotton.analytical.table',
    path: ROUTE_COTTON_ANALYTICAL,
    icon: <LayoutList color="white" />,
    active_icon: <LayoutList color="#2ED0FF" />,
    permissions: [PERMISSIONS_ENUM.VIEW_STATISTICS],
  },
];

export function LayoutSidebarApp() {
  const { t } = useTranslation();
  const { lang, type } = useParams();
  const { open, setOpen } = useSidebar();
  const [size, setSize] = useState({
    width: window.innerWidth,
  });
  // Track which parent menus are open (by path)
  const [openSubMenus, setOpenSubMenus] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const handleResize = () => {
      setSize({ width: window.innerWidth });
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    setOpen(size.width >= 2020);
  }, [size]);

  const { pathname } = useLocation();

  const isActive = (path: string, subItems?: ISidebarItem[]) => {
    const segments = pathname.split('/').filter(Boolean);
    const mainSection = segments[1];
    if (mainSection === path) return true;
    if (subItems && subItems.length > 0) {
      return subItems.some((sub) => segments.includes(sub.path));
    }
    return false;
  };
  const isSubActive = (path: string) => {
    const segments = pathname.split('/').filter(Boolean);
    return segments.includes(path);
  };

  const toggleSubMenu = (path: string) => {
    setOpenSubMenus((prev) => ({
      ...prev,
      [path]: !prev[path],
    }));
  };

  const { user, userLoading } = useStore();

  const filteredNavigationItems = navigationItems.filter((item) => {
    if (!item.permissions || item.permissions.length === 0) return true;
    return item.permissions.some((permission) =>
      user?.permissions?.includes(permission),
    );
  });

  return (
    <Sidebar collapsible="icon" variant="floating">
      <SidebarHeader
        className={cn(
          'px-3 pt-7 pb-0 duration-300',
          open ? '' : 'items-center',
        )}
      >
        <NavLink
          to={`/${lang}`}
          className={cn('flex flex-row items-center mb-6 w-fit cursor-pointer')}
        >
          <div className={'aspect-square [&>svg]:size-9 [&>svg]:shrink-0'}>
            <LogoShortIcon />
          </div>
          {open && (
            <p className="text-2xl font-bold ml-1.5">
              <span className="text-green-default">agro</span>
              <span className="text-white">.gov.uz</span>
            </p>
          )}
        </NavLink>

        <div>
          <SidebarArrowBgIcon
            className={'z-0 absolute -right-[6px] top-[16px]'}
          />
          <Button
            variant="ghost"
            size="icon"
            className={cn(
              'absolute -right-[12px] top-[37px] z-10 hover:bg-transparent',
            )}
            onClick={() => setOpen(!open)}
          >
            <ChevronRight
              className={cn('size-5 duration-500', open ? '-rotate-180' : '')}
              color="white"
            />
          </Button>
        </div>
      </SidebarHeader>

      <SidebarContent className={'my-4'}>
        <SidebarGroup className={cn('px-3')}>
          <SidebarGroupContent>
            <SidebarMenu>
              {user?.permissions?.includes(
                PERMISSIONS_ENUM.VIEW_STATISTICS,
              ) && <ReportsDialog layoutOpen={open} />}

              {filteredNavigationItems.map((item, i) => {
                const hasSubItems = item.sub_items && item.sub_items.length > 0;
                const isSubMenuOpen = openSubMenus[item.path];
                const active = isActive(item.path, item.sub_items);

                return (
                  <SidebarMenuItem key={i}>
                    {hasSubItems ? (
                      <>
                        {/* Parent item — navigates + toggles sub-menu */}
                        <SidebarMenuButton asChild>
                          <div
                            className={cn(
                              open
                                ? 'w-full !px-3'
                                : '!px-1 flex items-center justify-center',
                              active
                                ? 'bg-white/10 opacity-100'
                                : 'opacity-80 hover:bg-light hover:opacity-100',
                              'font-medium text-dark-default text-sm !py-4 duration-300 flex items-center cursor-pointer rounded-md',
                            )}
                          >
                            {/* Navigate to first sub item on icon/label click */}
                            <NavLink
                              to={`/${lang}/${item.path}`}
                              className="flex items-center gap-0  min-w-0"
                              onClick={() => toggleSubMenu(item.path)}
                            >
                              {active ? item.active_icon : item.icon}
                              {open && (
                                <span className="text-white ml-2 truncate">
                                  {t(item.title)}
                                </span>
                              )}
                            </NavLink>

                            {/* Chevron toggles sub-menu */}
                            {open && (
                              <ChevronDown
                                className={cn(
                                  'text-white ml-auto duration-300',
                                  isSubMenuOpen ? 'rotate-180' : '',
                                )}
                                style={{ width: 20, height: 20 }}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  toggleSubMenu(item.path);
                                }}
                              />
                            )}
                          </div>
                        </SidebarMenuButton>

                        {/* Sub items */}
                        {open && isSubMenuOpen && (
                          <div className="ml-4 mt-1 flex flex-col gap-1 border-l border-white/10 pl-3">
                            {item.sub_items!.map((sub, j) => {
                              const subActive = isSubActive(sub.path);
                              return (
                                <NavLink
                                  key={j}
                                  to={`/${lang}/${sub.path}`}
                                  className={cn(
                                    'flex items-center gap-2 px-3 py-2.5 rounded-md text-sm font-medium duration-300',
                                    subActive
                                      ? 'bg-white/10 opacity-100 text-[#2ED0FF]'
                                      : 'text-white opacity-70 hover:bg-light hover:opacity-100',
                                  )}
                                >
                                  {subActive ? sub.active_icon : sub.icon}
                                  <span>{t(sub.title)}</span>
                                </NavLink>
                              );
                            })}
                          </div>
                        )}
                      </>
                    ) : (
                      /* Regular item (no sub-menu) */
                      <SidebarMenuButton asChild>
                        <NavLink
                          to={
                            item.path === ROUTE_DOCS_LANDS
                              ? `/${lang}/${item.path}/LAND`
                              : item.path === ROUTE_DOCS_MONITORING
                                ? `/${lang}/${
                                    user?.region_id
                                      ? `${item.path}/${type || 'COTTON'}/${user?.region_id}`
                                      : `${item.path}/${type || 'COTTON'}`
                                  }`
                                : `/${lang}/${item.path}`
                          }
                          className={cn(
                            open
                              ? 'w-full !px-3'
                              : '!px-1 flex items-center justify-center',
                            active
                              ? 'bg-white/10 opacity-100'
                              : 'opacity-80 hover:bg-light hover:opacity-100',
                            'font-medium text-dark-default text-sm !py-4 duration-300 flex items-center',
                          )}
                        >
                          {active ? item.active_icon : item.icon}
                          {open && (
                            <span className={cn('text-white')}>
                              {t(item.title)}
                            </span>
                          )}
                        </NavLink>
                      </SidebarMenuButton>
                    )}
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter>
        <SidebarMenu className="px-3">
          <Language sidebarOpen={open} />
        </SidebarMenu>
        {!userLoading && user && <LayoutSidebarUser user={user} open={open} />}
      </SidebarFooter>
    </Sidebar>
  );
}
